import { useCallback, useEffect, useState } from "react";
import { api, API } from "./api";
import { Modal, Pager, StatusPill, Toolbar } from "./shared";

const ENQUIRY_STATUSES = ["new", "read", "replied", "closed"];
const APPLICATION_STATUSES = ["new", "reviewing", "shortlisted", "selected", "rejected"];

function usePagedList(path, extraDeps = []) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 10 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "10" });
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      setData(await api(`${path}?${params}`));
    } finally {
      setLoading(false);
    }
  }, [path, page, search, status]);
  useEffect(() => { load(); }, [load, ...extraDeps]);
  return { data, search, setSearch: (v) => { setSearch(v); setPage(1); }, status,
    setStatus: (v) => { setStatus(v); setPage(1); }, page, setPage, loading, reload: load };
}

export function EnquiriesView() {
  const { data, search, setSearch, status, setStatus, page, setPage, loading, reload } = usePagedList("/admin/enquiries");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [newStatus, setNewStatus] = useState("new");
  const [error, setError] = useState("");

  const open = (item) => { setSelected(item); setNotes(item.admin_notes || ""); setNewStatus(item.status); setError(""); };
  const save = async () => {
    try {
      await api(`/admin/enquiries/${selected.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, admin_notes: notes }) });
      setSelected(null); reload();
    } catch (err) { setError(err.message); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    await api(`/admin/enquiries/${selected.id}`, { method: "DELETE" });
    setSelected(null); reload();
  };

  return (
    <div data-testid="enquiries-view">
      <Toolbar search={search} onSearch={setSearch} status={status} onStatus={setStatus} statuses={ENQUIRY_STATUSES}
        placeholder="Search name, email or message…" />
      {loading ? <p className="admin-muted">Loading…</p> : (
        <table className="admin-table" data-testid="enquiries-table">
          <thead><tr><th>Received</th><th>Name</th><th>Email</th><th>Message</th><th>Status</th></tr></thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.id} onClick={() => open(item)} data-testid={`enquiry-row-${item.id}`}>
                <td>{new Date(item.created_at).toLocaleDateString()}</td>
                <td>{item.name}</td><td>{item.email}</td>
                <td className="admin-clip">{item.message}</td>
                <td><StatusPill value={item.status} /></td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan="5" className="admin-muted">No enquiries found.</td></tr>}
          </tbody>
        </table>
      )}
      <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      {selected && (
        <Modal title={`Enquiry from ${selected.name}`} onClose={() => setSelected(null)} testId="enquiry-modal">
          <div className="admin-detail">
            <p><span>Email</span><a href={`mailto:${selected.email}`}>{selected.email}</a></p>
            {selected.phone && <p><span>Phone</span>{selected.phone}</p>}
            {selected.subject && <p><span>Subject</span>{selected.subject}</p>}
            <p><span>Message</span>{selected.message}</p>
            <label>Status
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="enquiry-status-select">
                {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Admin notes
              <textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="enquiry-notes-input" />
            </label>
            {error && <p className="admin-error">{error}</p>}
            <div className="admin-modal-actions">
              <button className="button button-dark" onClick={save} data-testid="enquiry-save-button">Save</button>
              <button className="admin-danger" onClick={remove} data-testid="enquiry-delete-button">Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function ApplicationsView() {
  const { data, search, setSearch, status, setStatus, page, setPage, loading, reload } = usePagedList("/admin/applications");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [newStatus, setNewStatus] = useState("new");
  const [error, setError] = useState("");

  const open = (item) => { setSelected(item); setNotes(item.admin_notes || ""); setNewStatus(item.status); setError(""); };
  const save = async () => {
    try {
      await api(`/admin/applications/${selected.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, admin_notes: notes }) });
      setSelected(null); reload();
    } catch (err) { setError(err.message); }
  };
  const downloadResume = async () => {
    const response = await fetch(`${API}/admin/applications/${selected.id}/resume`, { credentials: "include" });
    if (!response.ok) { setError("Resume unavailable"); return; }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selected.name.replaceAll(" ", "_")}_resume`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="applications-view">
      <Toolbar search={search} onSearch={setSearch} status={status} onStatus={setStatus} statuses={APPLICATION_STATUSES}
        placeholder="Search name, email, college, position…" />
      {loading ? <p className="admin-muted">Loading…</p> : (
        <table className="admin-table" data-testid="applications-table">
          <thead><tr><th>Received</th><th>Name</th><th>Position</th><th>College</th><th>Resume</th><th>Status</th></tr></thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.id} onClick={() => open(item)} data-testid={`application-row-${item.id}`}>
                <td>{new Date(item.created_at).toLocaleDateString()}</td>
                <td>{item.name}</td><td>{item.position}</td><td>{item.college}</td>
                <td>{item.has_resume ? "Yes" : "—"}</td>
                <td><StatusPill value={item.status} /></td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan="6" className="admin-muted">No applications found.</td></tr>}
          </tbody>
        </table>
      )}
      <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)} testId="application-modal">
          <div className="admin-detail">
            <p><span>Email</span><a href={`mailto:${selected.email}`}>{selected.email}</a></p>
            <p><span>Phone</span>{selected.phone}</p>
            <p><span>College</span>{selected.college} · {selected.course}{selected.department ? ` · ${selected.department}` : ""} · {selected.year_of_study}</p>
            <p><span>Position</span>{selected.position}</p>
            {selected.skills && <p><span>Skills</span>{selected.skills}</p>}
            {(selected.portfolio_url || selected.github_url || selected.linkedin_url) && (
              <p><span>Links</span>
                {selected.portfolio_url && <a href={selected.portfolio_url} target="_blank" rel="noreferrer">Portfolio</a>}{" "}
                {selected.github_url && <a href={selected.github_url} target="_blank" rel="noreferrer">GitHub</a>}{" "}
                {selected.linkedin_url && <a href={selected.linkedin_url} target="_blank" rel="noreferrer">LinkedIn</a>}
              </p>
            )}
            <p><span>Cover message</span>{selected.cover_message}</p>
            {selected.has_resume && (
              <button className="admin-link-btn" onClick={downloadResume} data-testid="resume-download-button">Download resume ↓</button>
            )}
            <label>Status
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="application-status-select">
                {APPLICATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Admin notes
              <textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="application-notes-input" />
            </label>
            {error && <p className="admin-error">{error}</p>}
            <div className="admin-modal-actions">
              <button className="button button-dark" onClick={save} data-testid="application-save-button">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const emptyVenture = { name: "", category: "", short_description: "", detailed_description: "", website_url: "", status: "in_development", display_order: 5 };

export function VenturesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => { setItems(await api("/admin/ventures")); setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = JSON.stringify(editing.form);
      if (editing.id) {
        await api(`/admin/ventures/${editing.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body });
      } else {
        await api("/admin/ventures", { method: "POST", headers: { "Content-Type": "application/json" }, body });
      }
      setEditing(null); load();
    } catch (err) { setError(err.message); }
  };
  const archive = async (item) => {
    if (!window.confirm(`Archive ${item.name}? It will disappear from the website.`)) return;
    await api(`/admin/ventures/${item.id}`, { method: "DELETE" });
    load();
  };
  const uploadImage = async (file) => {
    if (!file || !editing?.id) return;
    const form = new FormData();
    form.append("file", file);
    await api(`/admin/ventures/${editing.id}/image`, { method: "POST", body: form });
    load();
  };
  const set = (key) => (e) => setEditing({ ...editing, form: { ...editing.form, [key]: e.target.value } });

  return (
    <div data-testid="ventures-view">
      <div className="admin-toolbar">
        <span className="admin-muted">{items.filter((v) => v.status !== "archived").length} live ventures</span>
        <button className="button button-dark" onClick={() => { setEditing({ id: null, form: { ...emptyVenture } }); setError(""); }}
          data-testid="venture-create-button">New venture</button>
      </div>
      {loading ? <p className="admin-muted">Loading…</p> : (
        <table className="admin-table" data-testid="ventures-table">
          <thead><tr><th>Order</th><th>Name</th><th>Category</th><th>Status</th><th>URL</th><th></th></tr></thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid={`venture-row-${item.slug}`}>
                <td>{item.display_order}</td><td>{item.name}</td><td>{item.category}</td>
                <td><StatusPill value={item.status} /></td>
                <td className="admin-clip">{item.website_url || "—"}</td>
                <td className="admin-row-actions">
                  <button onClick={() => { setEditing({ id: item.id, form: { name: item.name, category: item.category,
                    short_description: item.short_description, detailed_description: item.detailed_description || "",
                    website_url: item.website_url || "", status: item.status, display_order: item.display_order } }); setError(""); }}
                    data-testid={`venture-edit-${item.slug}`}>Edit</button>
                  {item.status !== "archived" && <button className="admin-danger" onClick={() => archive(item)}
                    data-testid={`venture-archive-${item.slug}`}>Archive</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <Modal title={editing.id ? "Edit venture" : "New venture"} onClose={() => setEditing(null)} testId="venture-modal">
          <div className="admin-detail">
            <label>Name<input value={editing.form.name} onChange={set("name")} data-testid="venture-name-input" /></label>
            <label>Category<input value={editing.form.category} onChange={set("category")} data-testid="venture-category-input" /></label>
            <label>Short description<textarea rows="2" value={editing.form.short_description} onChange={set("short_description")} data-testid="venture-description-input" /></label>
            <label>Detailed description<textarea rows="3" value={editing.form.detailed_description} onChange={set("detailed_description")} data-testid="venture-detailed-input" /></label>
            <label>Website URL<input value={editing.form.website_url} onChange={set("website_url")} placeholder="https://…" data-testid="venture-url-input" /></label>
            <div className="admin-two-col">
              <label>Status
                <select value={editing.form.status} onChange={set("status")} data-testid="venture-status-select">
                  <option value="active">active</option><option value="in_development">in development</option>
                  <option value="archived">archived</option>
                </select>
              </label>
              <label>Display order<input type="number" min="0" value={editing.form.display_order} onChange={set("display_order")} data-testid="venture-order-input" /></label>
            </div>
            {editing.id && (
              <label>Image / logo<input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => uploadImage(e.target.files[0])} data-testid="venture-image-input" /></label>
            )}
            {error && <p className="admin-error">{error}</p>}
            <div className="admin-modal-actions">
              <button className="button button-dark" onClick={save} data-testid="venture-save-button">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const emptyPosition = { title: "", type: "internship", department: "", location: "", description: "", requirements: "", status: "draft", application_deadline: "" };

export function PositionsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => { setItems(await api("/admin/positions")); setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...editing.form };
      if (!payload.application_deadline) delete payload.application_deadline;
      const body = JSON.stringify(payload);
      if (editing.id) {
        await api(`/admin/positions/${editing.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body });
      } else {
        await api("/admin/positions", { method: "POST", headers: { "Content-Type": "application/json" }, body });
      }
      setEditing(null); load();
    } catch (err) { setError(err.message); }
  };
  const quickStatus = async (item, status) => {
    await api(`/admin/positions/${item.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }) });
    load();
  };
  const set = (key) => (e) => setEditing({ ...editing, form: { ...editing.form, [key]: e.target.value } });

  return (
    <div data-testid="positions-view">
      <div className="admin-toolbar">
        <span className="admin-muted">{items.filter((p) => p.status === "open").length} open positions</span>
        <button className="button button-dark" onClick={() => { setEditing({ id: null, form: { ...emptyPosition } }); setError(""); }}
          data-testid="position-create-button">New position</button>
      </div>
      {loading ? <p className="admin-muted">Loading…</p> : (
        <table className="admin-table" data-testid="positions-table">
          <thead><tr><th>Title</th><th>Type</th><th>Location</th><th>Deadline</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid={`position-row-${item.id}`}>
                <td>{item.title}</td><td>{item.type}</td><td>{item.location || "—"}</td>
                <td>{item.application_deadline || "—"}</td>
                <td><StatusPill value={item.status} /></td>
                <td className="admin-row-actions">
                  <button onClick={() => { setEditing({ id: item.id, form: { title: item.title, type: item.type,
                    department: item.department || "", location: item.location || "", description: item.description,
                    requirements: item.requirements || "", status: item.status,
                    application_deadline: item.application_deadline || "" } }); setError(""); }}
                    data-testid={`position-edit-${item.id}`}>Edit</button>
                  {item.status === "draft" && <button onClick={() => quickStatus(item, "open")} data-testid={`position-publish-${item.id}`}>Publish</button>}
                  {item.status === "open" && <button onClick={() => quickStatus(item, "closed")} data-testid={`position-close-${item.id}`}>Close</button>}
                  {item.status !== "archived" && <button className="admin-danger" onClick={() => quickStatus(item, "archived")} data-testid={`position-archive-${item.id}`}>Archive</button>}
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan="6" className="admin-muted">No positions yet.</td></tr>}
          </tbody>
        </table>
      )}
      {editing && (
        <Modal title={editing.id ? "Edit position" : "New position"} onClose={() => setEditing(null)} testId="position-modal">
          <div className="admin-detail">
            <label>Title<input value={editing.form.title} onChange={set("title")} data-testid="position-title-input" /></label>
            <div className="admin-two-col">
              <label>Type
                <select value={editing.form.type} onChange={set("type")} data-testid="position-type-select">
                  <option value="internship">internship</option><option value="full-time">full-time</option>
                  <option value="part-time">part-time</option><option value="contract">contract</option>
                </select>
              </label>
              <label>Status
                <select value={editing.form.status} onChange={set("status")} data-testid="position-status-select">
                  <option value="draft">draft</option><option value="open">open</option>
                  <option value="closed">closed</option><option value="archived">archived</option>
                </select>
              </label>
            </div>
            <div className="admin-two-col">
              <label>Department<input value={editing.form.department} onChange={set("department")} data-testid="position-department-input" /></label>
              <label>Location<input value={editing.form.location} onChange={set("location")} data-testid="position-location-input" /></label>
            </div>
            <label>Application deadline<input type="date" value={editing.form.application_deadline} onChange={set("application_deadline")} data-testid="position-deadline-input" /></label>
            <label>Description<textarea rows="3" value={editing.form.description} onChange={set("description")} data-testid="position-description-input" /></label>
            <label>Requirements<textarea rows="3" value={editing.form.requirements} onChange={set("requirements")} data-testid="position-requirements-input" /></label>
            {error && <p className="admin-error">{error}</p>}
            <div className="admin-modal-actions">
              <button className="button button-dark" onClick={save} data-testid="position-save-button">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
